import React from 'react';
import { Card } from '../../../components/Card/Card';
import { NoData } from '../../../components/NoData/NoData';
import { graphql } from '../../../generated';
import { t } from '../../../locales/translate';
import { withSuspendedCard } from '../../../util/suspense';
import { AreaChart, Dataset } from '../../../components/Charts/AreaChart';
import { Text } from '../../../components/text/text';
import { useSuspenseQuery } from '@apollo/client';
import { SalesOverTimePeriod } from '../../../generated/graphql';
import { Icon } from '../../../components/Icon/Icon';
import { Stack } from '../../../components/Stack/Stack';
import { Tooltip } from '../../../components/Tooltip/Tooltip';
import { formatNumber } from '../../../util/formatters';

const PLACED_ORDERS = graphql(`
  query PlacedOrders($from: ISO8601DateTime, $to: ISO8601DateTime, $period: SalesOverTimePeriod) {
    statistics {
      placedOrderCountOverTime(during: { from: $from, to: $to }, period: $period) {
        total
        result {
          date
          total
        }
      }
    }
  }
`);

interface Props {
  from: Date;
  to: Date;
  period: SalesOverTimePeriod;
}

function OrdersPlaced$({ from, to, period }: Props): JSX.Element | null {
  const { data } = useSuspenseQuery(PLACED_ORDERS, {
    variables: {
      from: from,
      to: to,
      period: period,
    },
  });

  const total = data?.statistics?.placedOrderCountOverTime?.total;
  const placedOrderCountOverTimeData = data?.statistics?.placedOrderCountOverTime;
  const dataset: Dataset[] = [
    {
      title: t.component.ordersPlaced.period[period],
      values: placedOrderCountOverTimeData?.result.map((e) => e.total) || [],
      color: '#00EDFF80',
    },
  ];

  return (
    <Card
      size="dashboard"
      title={
        <Stack line size="xxs" align="center">
          <p>{t.component.ordersPlaced.title}</p>
          <Tooltip small padding="medium" position="bottom" reference={<Icon name="info-circle" color="#909090" />}>
            <Text type="tooltip-content">{t.component.ordersPlaced.tooltip}</Text>
          </Tooltip>
        </Stack>
      }
      actions={
        placedOrderCountOverTimeData &&
        placedOrderCountOverTimeData.result.length > 0 && (
          <Text type="card-label">
            {t.component.ordersPlaced.total} {formatNumber(total, 0)}
          </Text>
        )
      }
    >
      {placedOrderCountOverTimeData && placedOrderCountOverTimeData.result.length > 0 ? (
        <AreaChart
          style={{ height: 249 }}
          datasets={dataset}
          labels={placedOrderCountOverTimeData.result.map((e) => e.date)}
          showTimeOnXAxis={period === 'hour'}
        />
      ) : (
        <NoData description={t.component.ordersPlaced.noData} />
      )}
    </Card>
  );
}

export const OrdersPlaced = withSuspendedCard(OrdersPlaced$, {
  loadingTitle: t.component.ordersPlaced.title,
  loadingHeight: 348,
});
